// Bot detection utilities for filtering non-human traffic

const BOT_PATTERNS = [
  'bot', 'crawler', 'spider', 'headless', 'phantom', 'selenium', 'puppeteer',
  'lighthouse', 'googlebot', 'bingbot', 'yandex', 'baidu', 'slurp', 'duckduckbot',
  'facebookexternalhit', 'twitterbot', 'linkedinbot', 'whatsapp', 'telegram',
  'discordbot', 'applebot', 'msnbot', 'ahrefsbot', 'semrushbot', 'dotbot',
  'rogerbot', 'screaming frog', 'gtmetrix', 'pingdom', 'uptimerobot',
  'mediapartners', 'adsbot', 'apis-google', 'feedfetcher', 'cloud functions',
  'prerender', 'wget', 'curl', 'httpie', 'python-requests', 'go-http-client',
  'java', 'libwww', 'apache-httpclient', 'okhttp', 'node-fetch', 'axios'
];

const HEADLESS_INDICATORS = [
  () => navigator.webdriver === true,
  () => !navigator.languages || navigator.languages.length === 0,
  () => window.outerWidth === 0 || window.outerHeight === 0,
  () => navigator.plugins?.length === 0,
  () => !(window as unknown as Record<string, unknown>).chrome && navigator.userAgent.includes('Chrome'),
];

export function isLikelyBot(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return true;
  }

  const ua = navigator.userAgent.toLowerCase();

  // Check for known bot user agent patterns
  if (BOT_PATTERNS.some(pattern => ua.includes(pattern))) {
    return true;
  }

  // Check for headless browser indicators
  for (const check of HEADLESS_INDICATORS) {
    try {
      if (check()) return true;
    } catch {
      // Ignore errors in detection checks
    }
  }

  // Check for suspicious navigator properties
  if (!navigator.cookieEnabled) return true;

  return false;
}

export function getDeviceType(): 'mobile' | 'tablet' | 'desktop' {
  if (typeof window === 'undefined') return 'desktop';
  
  const ua = navigator.userAgent.toLowerCase();
  
  if (/ipad|tablet|playbook|silk/.test(ua) || 
      (ua.includes('android') && !ua.includes('mobile'))) {
    return 'tablet';
  }
  
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile|wpdesktop/.test(ua)) {
    return 'mobile';
  }
  
  return 'desktop';
}

export function getBrowser(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  
  const ua = navigator.userAgent;
  
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('OPR/') || ua.includes('Opera/')) return 'Opera';
  if (ua.includes('Safari/') && !ua.includes('Chrome/')) return 'Safari';
  if (ua.includes('Chrome/')) return 'Chrome';
  
  return 'Other';
}

export function getOS(): string {
  if (typeof navigator === 'undefined') return 'Unknown';
  
  const ua = navigator.userAgent;
  
  if (ua.includes('Windows NT 10')) return 'Windows 10';
  if (ua.includes('Windows NT 6.3')) return 'Windows 8.1';
  if (ua.includes('Windows NT')) return 'Windows';
  if (ua.includes('Mac OS X')) return 'macOS';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('CrOS')) return 'Chrome OS';
  
  return 'Other';
}

export function getScreenResolution(): string {
  if (typeof window === 'undefined') return 'Unknown';
  return `${window.screen.width}x${window.screen.height}`;
}

export function getReferrerSource(referrer: string): string {
  if (!referrer) return 'Direct';
  
  const url = new URL(referrer);
  const hostname = url.hostname.toLowerCase();
  
  // Search engines
  if (hostname.includes('google')) return 'Google';
  if (hostname.includes('bing')) return 'Bing';
  if (hostname.includes('yahoo')) return 'Yahoo';
  if (hostname.includes('duckduckgo')) return 'DuckDuckGo';
  
  // Social networks
  if (hostname.includes('facebook') || hostname.includes('fb.com')) return 'Facebook';
  if (hostname.includes('twitter') || hostname.includes('t.co') || hostname.includes('x.com')) return 'X';
  if (hostname.includes('linkedin')) return 'LinkedIn';
  if (hostname.includes('instagram')) return 'Instagram';
  if (hostname.includes('youtube')) return 'YouTube';
  if (hostname.includes('tiktok')) return 'TikTok';
  if (hostname.includes('reddit')) return 'Reddit';
  
  return hostname;
}

export function getUTMParams(): {
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
} {
  if (typeof window === 'undefined') {
    return { utm_source: null, utm_medium: null, utm_campaign: null, utm_term: null, utm_content: null };
  }
  
  const params = new URLSearchParams(window.location.search);
  
  return {
    utm_source: params.get('utm_source'),
    utm_medium: params.get('utm_medium'),
    utm_campaign: params.get('utm_campaign'),
    utm_term: params.get('utm_term'),
    utm_content: params.get('utm_content'),
  };
}
